import { useState, type ReactNode } from 'react'
import { Plus } from 'lucide-react'
import type { Share } from '@/api'
import { useAction, useDevices, useShares } from '@/api/hooks'
import { copy } from '@/copy'
import { ShareCard } from '@/components/ShareCard'
import { CommandGrid, InstallLink, Start, Step, Steps } from '@/components/Commands'
import { Terminal } from '@/components/Terminal'
import { PageHead } from '@/components/Shell'
import { Failed, Loading } from '@/components/States'
import { useConsole } from '@/lib/store'
import { useNow } from '@/lib/useNow'
import { useTitle } from '@/lib/useTitle'

const shareCommands = [
  { label: copy.shares.anyoneCommand, command: copy.commands.share },
  { label: copy.shares.namedCommand, command: copy.commands.shareTo },
]

// Shares: every live share of the account, from every device (D-014, D-052).
// Ended shares disappear; there is no history.
export function Shares() {
  useTitle(copy.titles.shares)
  const shares = useShares()
  // Nothing shared and no device signed in: someone new, who needs every step.
  const devices = useDevices()
  const fresh = devices.data?.devices.length === 0
  const now = useNow()
  const [starting, setStarting] = useState(false)
  const announce = useConsole((s) => s.announce)
  const stop = useAction((api, csrf, share: Share) => api.stopShare(csrf, share.label))

  const onStop = async (share: Share) => {
    try {
      await stop.mutateAsync(share)
      announce(copy.shares.stopped)
      return true
    } catch {
      return false
    }
  }

  const list = shares.data?.shares.filter((s) => Date.parse(s.expires_at) > now)
  let body: ReactNode
  if (!shares.data && shares.isError) body = <Failed what={copy.shares.failed} onRetry={() => void shares.refetch()} busy={shares.isFetching} />
  else if (!list) body = <Loading rows={2} />
  else if (list.length === 0)
    body = fresh ? (
      <Start heading={copy.shares.empty} line={copy.shares.firstLine}>
        <Steps>
          <Step n={1} title={copy.start.installStep} line={copy.start.installLine}>
            <InstallLink />
          </Step>
          <Step n={2} title={copy.start.loginStep} line={copy.start.loginLine}>
            <div className="max-w-[520px]">
              <Terminal command={copy.commands.login} />
            </div>
          </Step>
          <Step n={3} title={copy.start.shareStep} line={copy.start.shareLine}>
            <CommandGrid commands={shareCommands} />
          </Step>
        </Steps>
      </Start>
    ) : (
      <Start heading={copy.shares.empty} line={copy.shares.emptyLine}>
        <CommandGrid commands={shareCommands} />
      </Start>
    )
  else
    body = (
      <section className="card overflow-hidden" aria-label={copy.shares.heading}>
        <div className="share-heads hidden items-center border-b border-rule px-4 py-2 text-[13px] text-secondary min-[1024px]:grid" aria-hidden="true">
          <span className="pl-10">{copy.shares.app}</span>
          <span>{copy.shares.from}</span>
          <span>{copy.shares.whoGetsIn}</span>
          <span>{copy.shares.timeLeft}</span>
          <span />
        </div>
        <ul className="m-0 list-none p-0">
          {list.map((s) => (
            <li key={s.id} className="m-0 border-t border-rule first:border-t-0">
              <ShareCard share={s} now={now} onStop={onStop} />
            </li>
          ))}
        </ul>
      </section>
    )

  const live = list && list.length > 0
  return (
    <>
      <PageHead
        label={live ? copy.shares.live(list.length) : undefined}
        title={copy.shares.heading}
        action={
          live && (
            <button type="button" className="action" aria-expanded={starting} aria-controls="new-share" onClick={() => setStarting((s) => !s)}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {copy.shares.newShare}
            </button>
          )
        }
      />
      {live && (
        <section id="new-share" className="card mb-4 px-4 py-4" hidden={!starting} aria-label={copy.shares.newShare}>
          <p className="m-0 mb-3 text-[14px] leading-5 text-secondary">{copy.shares.newShareLine}</p>
          <CommandGrid commands={shareCommands} />
        </section>
      )}
      {body}
    </>
  )
}
